import { Close, ImageOutlined, Search, UploadOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import Loading from "../../../../components/loading/loading";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { getMediaList, MAX_MEDIA_FILE_SIZE_BYTES, MAX_MEDIA_FILE_SIZE_LABEL, uploadMedia } from "../../../../services/cms/cms";
import { greyEnabled, purple, purpleLight } from "../../../../theme/colors";
import { Media, mediaDimensionsLabel } from "../../models/cms";

interface IconPickerProps {
  label: string;
  value?: string;
  disabled?: boolean;
  required?: boolean;
  onChange: (url: string) => void;
  // e.g. "1:1" — when given, the empty-state placeholder is drawn in that
  // shape (instead of a plain button) so the user can see what's expected.
  recommendedAspectRatio?: string;
  // e.g. "100 x 100 px" — an optional concrete resolution hint.
  recommendedSizeLabel?: string;
}

function isImage(item: Media): boolean {
  return !!item.mime_type?.startsWith("image/");
}

export default function IconPicker({
  label,
  value,
  disabled,
  required,
  onChange,
  recommendedAspectRatio,
  recommendedSizeLabel,
}: IconPickerProps) {
  const { showSnackbar } = useFeedback();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadAltText, setUploadAltText] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const { data: mediaList, isLoading, isFetching, refetch } = useQuery(
    ["cms-media-picker-icon", search],
    async () => {
      const response = await getMediaList({ page: 1, size: 50, search });
      return response?.data?.data;
    },
    { enabled: open, keepPreviousData: true }
  );

  const imageList: Media[] = (mediaList?.list || []).filter(isImage);

  useEffect(() => {
    if (open) refetch();
  }, [open]);

  const selectPendingFile = (file: File) => {
    if (file.size > MAX_MEDIA_FILE_SIZE_BYTES) {
      setSizeError(`File is too large. Maximum allowed size is ${MAX_MEDIA_FILE_SIZE_LABEL}.`);
      return;
    }
    setSizeError(null);
    setPendingFile(file);
    setUploadTitle(file.name);
    setUploadAltText("");
    setPreviewUrl(URL.createObjectURL(file));
  };

  const clearPendingFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(null);
    setPreviewUrl(null);
    setSizeError(null);
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const response = await uploadMedia(pendingFile, uploadTitle, uploadAltText);
      const media: Media = response?.data?.data;
      onChange(media.file_url);
      clearPendingFile();
      setOpen(false);
    } catch (e: any) {
      showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        {label}
        {required && <span style={{ color: "red" }}> *</span>}
      </Typography>

      {value ? (
        <Box>
          <Box
            component="img"
            src={value}
            sx={{ width: 100, height: 100, objectFit: "cover", borderRadius: 1, mb: 1, border: "1px solid #E0E0E0" }}
          />
          <Box display="flex" alignItems="center" gap={2}>
            <Button variant="outlined" size="small" disabled={disabled} onClick={() => setOpen(true)}>
              Change
            </Button>
            {!disabled && (
              <IconButton size="small" onClick={() => onChange("")}>
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      ) : recommendedAspectRatio ? (
        <Box
          onClick={() => !disabled && setOpen(true)}
          sx={{
            width: 100,
            maxWidth: "100%",
            aspectRatio: recommendedAspectRatio.replace(":", " / "),
            border: "2px dashed #ABA0AB",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            p: 1,
            bgcolor: "#FAFAFA",
            cursor: disabled ? "default" : "pointer",
            transition: "border-color 0.15s ease, background-color 0.15s ease",
            "&:hover": disabled ? undefined : { borderColor: purple, bgcolor: purpleLight },
          }}
        >
          <ImageOutlined sx={{ color: greyEnabled, fontSize: 20 }} />
          <Typography variant="caption" color={purple} fontWeight={600} textAlign="center">
            {recommendedSizeLabel || `${recommendedAspectRatio} ratio`}
          </Typography>
        </Box>
      ) : (
        <Button variant="outlined" startIcon={<UploadOutlined />} disabled={disabled} onClick={() => setOpen(true)}>
          Select Icon
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          clearPendingFile();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select {label}
          <IconButton
            onClick={() => {
              setOpen(false);
              clearPendingFile();
            }}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Select Existing" />
            <Tab label="Upload New" />
          </Tabs>

          {tab === 0 && (
            <Box>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search images"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              {isLoading || isFetching ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <Loading />
                </Box>
              ) : !imageList.length ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <Typography color="text.secondary">No images found</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                    gap: 2,
                    maxHeight: 420,
                    overflowY: "auto",
                  }}
                >
                  {imageList.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => {
                        onChange(item.file_url);
                        setOpen(false);
                      }}
                      sx={{
                        cursor: "pointer",
                        border: value === item.file_url ? "2px solid #91278F" : "1px solid #E0E0E0",
                        borderRadius: 2,
                        overflow: "hidden",
                        p: 0.5,
                      }}
                    >
                      <Box
                        component="img"
                        src={item.file_url}
                        sx={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 1, bgcolor: "#F5F5F5" }}
                      />
                      <Typography variant="caption" noWrap display="block" mt={0.5}>
                        {item.title || item.file_path}
                      </Typography>
                      {mediaDimensionsLabel(item) && (
                        <Typography variant="caption" noWrap display="block" color="text.secondary">
                          {mediaDimensionsLabel(item)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              {!pendingFile ? (
                <>
                  <Box
                    component="label"
                    sx={{
                      width: 160,
                      aspectRatio: (recommendedAspectRatio || "1:1").replace(":", " / "),
                      border: "2px dashed #ABA0AB",
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      p: 1,
                      bgcolor: "#FAFAFA",
                      cursor: "pointer",
                      transition: "border-color 0.15s ease, background-color 0.15s ease",
                      "&:hover": { borderColor: purple, bgcolor: purpleLight },
                    }}
                  >
                    <ImageOutlined sx={{ color: greyEnabled }} />
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      Choose Icon
                    </Typography>
                    {recommendedSizeLabel && (
                      <Typography variant="caption" color={purple} fontWeight={600} textAlign="center">
                        {recommendedSizeLabel}
                      </Typography>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) selectPendingFile(file);
                        e.target.value = "";
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color={sizeError ? "error" : "text.secondary"} mt={1}>
                    {sizeError || `Max file size: ${MAX_MEDIA_FILE_SIZE_LABEL}`}
                  </Typography>
                </>
              ) : (
                <Box width="100%" maxWidth={360}>
                  {previewUrl && (
                    <Box
                      component="img"
                      src={previewUrl}
                      sx={{ width: 160, aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 1, mb: 1, mx: "auto", display: "block" }}
                    />
                  )}
                  <TextField
                    fullWidth
                    required
                    label={<>Title <span style={{ color: "red" }}>*</span></>}
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    margin="normal"
                    disabled={uploading}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Alt Text"
                    value={uploadAltText}
                    onChange={(e) => setUploadAltText(e.target.value)}
                    margin="normal"
                    disabled={uploading}
                    size="small"
                  />
                  <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                    <Button onClick={clearPendingFile} disabled={uploading}>
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleUpload} disabled={uploading || !uploadTitle.trim()}>
                      {uploading ? "Uploading..." : "Upload"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
